// Post-call email follow-up panel (SPEC §19, 2026-10-02). Opened from the wrap-up dock ("Send email") on a connected
// call, or by picking the "Send me info" objection. Address, kind of email, two optional fields, Draft (the server
// transcribes the private recording if there is one and drafts with Claude), then the editable draft and Send. Nothing
// is ever sent without the Send click. After a send: the follow-up call task exists and, if no outcome is picked yet,
// the outcome becomes Callback at the follow-up time (the setter can still change it).
import { useEffect, useState } from 'preact/hooks';
import type { QueueItem } from '../../../src/shared/api';
import { SALES_EMAIL_TYPES, type SalesEmailContext, type SalesEmailDraft, type SalesEmailType } from '../../../src/shared/api-sales-email';
import { ApiFailure, errorMessage } from '../api';
import { ErrorBanner, toast } from '../components/ui';
import { Field, Spinner } from '../components/ui-extra';
import { endpoints } from './endpoints';
import { followUpLabel, sendBlockReason } from './email';
import { defaultEmailType } from './emailDefault';
import { SendBar } from './EmailSendBar';
import { chooseDisposition, updateWrap, type WrapUp } from './store';

export function EmailPanel(props: { item: QueueItem; callId: number | null; wrap: WrapUp; canSetOutcome: boolean; onClose: () => void }) {
  const b = props.item.business;
  const callId = props.callId;
  const [ctx, setCtx] = useState<SalesEmailContext | null>(null);
  const [ctxError, setCtxError] = useState<string | null>(null);
  // An address the setter typed in the wrap-up; the business's address on file comes from the server (never for Canada).
  const [to, setTo] = useState(props.wrap.email && props.wrap.email !== b.email ? props.wrap.email : '');
  const [consent, setConsent] = useState(false);
  // Opens on the campaign's offer (Get Found on a website_seo campaign). The Dialer closes the panel on a new lead
  // (Dialer.tsx leadId effect), so each lead's first open starts from its own default.
  const [type, setType] = useState<SalesEmailType>(() => defaultEmailType(props.item));
  const [caredAbout, setCaredAbout] = useState('');
  const [mention, setMention] = useState('');
  const [draft, setDraft] = useState<SalesEmailDraft | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [override, setOverride] = useState(false);
  const [busy, setBusy] = useState<'draft' | 'send' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);   // shown next to Send (validation or delivery)
  const [pending, setPending] = useState(false);       // 409 recording_pending: offer "Draft from my notes only"
  const [sent, setSent] = useState<string | null>(null);

  const load = () => {
    if (callId === null) return;
    endpoints.emailContext(callId).then((c) => {
      setCtx(c);
      setCtxError(null);
      setTo((cur) => cur || c.prefillEmail || c.transcriptEmail || '');
    }, (e) => setCtxError(errorMessage(e)));
  };
  useEffect(load, [callId]);

  if (callId === null) return <div class="vd-email"><p class="muted">Dial first: the email follows up on a call.</p></div>;

  const heard = draft?.transcriptEmail ?? ctx?.transcriptEmail ?? null;
  const doDraft = async (useRecording?: boolean) => {
    setBusy('draft'); setError(null); setPending(false);
    try {
      const d = await endpoints.emailDraft(callId, {
        type, caredAbout: caredAbout.trim() || undefined, mention: mention.trim() || undefined,
        notes: props.wrap.note.trim() || undefined, contactName: props.wrap.dmName.trim() || b.dmName || undefined,
        ...(useRecording === false ? { useRecording: false } : {}),
      });
      setDraft(d); setSubject(d.subject); setBody(d.body); setSendError(null);
      if (d.transcriptEmail && !to.trim()) setTo(d.transcriptEmail);
    } catch (e) {
      if (e instanceof ApiFailure && /^(recording_|transcription_)/.test(e.body.error)) setPending(true);   // offer notes-only
      setError(errorMessage(e));
    } finally { setBusy(null); load(); }
  };

  const form = { to, subject, body, consentOnCall: consent, ownerOverride: override };
  const why = busy ? null : sendBlockReason(ctx, draft, form);
  const doSend = async () => {
    if (!draft) return;
    setBusy('send'); setError(null); setSendError(null);
    try {
      const r = await endpoints.emailSend(callId, {
        draftId: draft.draftId, to: to.trim(), subject: subject.trim(), body,
        ...(ctx?.canadaNeedsConsent ? { consentOnCall: consent } : {}), ...(override ? { ownerOverride: true } : {}),
      });
      const when = r.followUp ? followUpLabel(r.followUp.dueAt, b.tz) : null;
      setSent(`Sent to ${r.email.to}.${when ? ` Follow-up call: ${when} their time.` : ''}`);
      toast(`Email sent to ${r.email.to}`, 'ok');
      const w = props.wrap;
      const note = `${w.note.trim() ? `${w.note.trim()}\n` : ''}Emailed ${r.email.to}: ${r.email.subject ?? ''}`;
      updateWrap({ note, email: w.email || r.email.to || '', objection: w.objection || 'send_info' });
      // The outcome for "send me info" is a callback at the follow-up time, unless the setter already picked one.
      if (props.canSetOutcome && !w.code && r.followUp) {
        chooseDisposition('callback_set');
        updateWrap({ callbackAt: r.followUp.dueAt });
      }
      setDraft(null);
    } catch (e) {
      setSendError(errorMessage(e));          // the server's own sentence, right above the Send button
    } finally { setBusy(null); load(); }
  };

  return (
    <div class="vd-email stack">
      <div class="row" style="justify-content:space-between">
        <span class="eyebrow">Email {b.name}</span>
        <button class="btn btn-sm" onClick={props.onClose}>Close <kbd>Esc</kbd></button>
      </div>
      {ctxError ? <ErrorBanner error={ctxError} /> : null}
      {!ctx && !ctxError ? <Spinner label="Loading…" /> : null}
      {ctx && !ctx.connected ? <p class="vd-bad">This call didn't connect, so there's no one to email.</p> : null}
      {ctx?.sendBlocked ? <div class="adm-muted-box vd-bad">{ctx.sendBlocked}</div> : null}
      {sent ? <div class="adm-muted-box">{sent}</div> : null}

      <Field label="Email address">
        <input type="email" value={to} placeholder="name@business.com" onInput={(e) => setTo(e.currentTarget.value)} />
      </Field>
      {heard && heard !== to.trim().toLowerCase() ? (
        <div class="row" style="gap:6px"><span class="muted">Heard on the call: <b>{heard}</b></span>
          <button class="btn btn-sm" onClick={() => setTo(heard)}>Use this</button></div>
      ) : null}
      {ctx?.canadaNeedsConsent ? (
        <label class="row" style="gap:6px;align-items:flex-start">
          <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.currentTarget.checked)} />
          <span>They gave me this address on this call and asked for the email. <span class="muted">(Canada, CASL: never an address from their website or a list.)</span></span>
        </label>
      ) : null}

      <Field label="Kind of email">
        <select value={type} onChange={(e) => setType(e.currentTarget.value as SalesEmailType)}>
          {SALES_EMAIL_TYPES.map((t) => <option value={t.key} title={t.hint}>{t.label}</option>)}
        </select>
      </Field>
      <Field label="What did they care about?" hint="Optional">
        <input value={caredAbout} maxLength={500} onInput={(e) => setCaredAbout(e.currentTarget.value)} />
      </Field>
      <Field label="Anything to mention?" hint="Optional">
        <input value={mention} maxLength={500} onInput={(e) => setMention(e.currentTarget.value)} />
      </Field>
      {ctx ? <p class="muted" style="margin:0;font-size:13px">{ctx.recording.note}</p> : null}

      <div class="row" style="gap:8px;flex-wrap:wrap">
        <button class="btn btn-primary" disabled={!ctx || !!ctx.draftBlocked || busy !== null} title={ctx?.draftBlocked ?? undefined} onClick={() => void doDraft()}>
          {busy === 'draft' ? 'Drafting…' : draft ? 'Draft again' : 'Draft'}
        </button>
        {pending ? <button class="btn" disabled={busy !== null} onClick={() => void doDraft(false)}>Draft from my notes only</button> : null}
        {ctx ? <span class="muted" style="font-size:12px">{ctx.draftsLeftToday} drafts left today</span> : null}
      </div>
      {busy === 'draft' ? <span class="muted" style="font-size:13px">Transcribing the call can take up to a minute.</span> : null}
      {ctx?.draftBlocked ? <span class="vd-field-hint">{ctx.draftBlocked}</span> : null}
      <ErrorBanner error={error} />

      {draft ? (
        <div class="stack">
          <span class="muted" style="font-size:13px">{draft.basisNote}</span>
          {draft.facts.length ? (
            <details><summary class="muted" style="font-size:13px">Facts it could use ({draft.facts.length})</summary>
              <ul style="margin:4px 0;font-size:13px">{draft.facts.map((f) => <li>{f}</li>)}</ul></details>
          ) : draft.type === 'audit' ? <span class="muted" style="font-size:13px">Nothing is measured for this business yet, so the email only offers the audit.</span> : null}
          <span class="muted" style="font-size:13px">From: {draft.from}</span>
          <Field label="Subject"><input value={subject} maxLength={150} onInput={(e) => setSubject(e.currentTarget.value)} /></Field>
          <Field label="Email (edit freely; nothing sends until you press Send)">
            <textarea rows={12} value={body} onInput={(e) => setBody(e.currentTarget.value)} />
          </Field>
          <span class="muted" style="font-size:12px">Your signature is added under the email:</span>
          <pre class="muted" style="white-space:pre-wrap;font-size:12px;margin:0">{draft.signature}</pre>
          <pre class="muted" style="white-space:pre-wrap;font-size:12px;margin:0">{draft.footerPreview}</pre>
          {ctx?.nextAllowedAt && ctx.ownerCanOverride ? (
            <label class="row" style="gap:6px"><input type="checkbox" checked={override} onChange={(e) => setOverride(e.currentTarget.checked)} />
              <span>Send anyway (owner): this business was emailed {ctx.lastSentAt ? new Date(ctx.lastSentAt).toLocaleDateString() : 'this week'}.</span></label>
          ) : null}
          <SendBar why={why} sending={busy === 'send'} disabled={busy !== null} error={sendError} onSend={() => void doSend()} />
        </div>
      ) : null}

      {ctx?.sent.length ? (
        <div class="stack" style="gap:2px">
          <span class="eyebrow">Sent on this call</span>
          {ctx.sent.map((m) => (
            <span class="muted" style="font-size:13px">{m.status === 'sent' ? 'Sent' : 'Failed'} {m.sentAt ? new Date(m.sentAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : ''} · {m.typeLabel} · {m.to} · {m.subject}</span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

